import { FC, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { RefreshCw } from "lucide-react"
import { Link } from "react-router-dom"

import Table from "../../table"
import TimestampCell from "./cells/timestamp"
import DescriptionCell from "./cells/description"
import If from "../../if"
import SidePanel from "../../side-panel"
import ActivitySkeleton from "../../skeletons/dashboard/activity"
import ActivityDetailsFull from "../../activity-details-full"
import { useFetchOrgActivitiesQuery } from "../../../../redux/queries"
import { getOrgBrandsIDs } from "../../../utils"
import { OrganisationTab } from "../../../constants"
import { BBColumnDef, OrgActivity, Organization } from "../../../../types"

interface Props {
  orgDetails?: Organization
}

const DashboardActivityTable: FC<Props> = ({ orgDetails }) => {
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null)

  const {
    data: orgActivities,
    isFetching: isFetchingActivities,
    refetch: refetchOrgActivities
  } = useFetchOrgActivitiesQuery(
    {
      group_id: getOrgBrandsIDs(orgDetails),
      range_start: parseInt(
        DateTime.local()
          .minus({ days: 90 })
          .startOf("day")
          .toSeconds()
          .toString()
      ),
      range_end: parseInt(DateTime.local().endOf("day").toSeconds().toString()),
      limit: 9999
    },
    { skip: !orgDetails?.id }
  )

  const activityDetails = useMemo(
    () => orgActivities?.find(item => item.event_id === selectedActivity),
    [orgActivities, selectedActivity]
  )

  const columns: BBColumnDef<OrgActivity>[] = [
    {
      id: "description",
      header: "Description",
      accessorFn: row => row.event_id,
      footer: props => props.column.id,
      cell: props => (
        <DescriptionCell
          info={props.row.original}
          onClick={setSelectedActivity}
        />
      )
    },
    {
      id: "timestamp",
      header: "Timestamp",
      accessorFn: row => row.timestamp,
      footer: props => props.column.id,
      cell: props => <TimestampCell info={props.row.original} />
    }
  ]

  const clearSelectedActivity = () => setSelectedActivity(null)

  return (
    <>
      <If
        condition={isFetchingActivities}
        then={<ActivitySkeleton />}
        else={
          <div className="bg-white flex flex-col rounded-md pl-6 pr-2">
            <div className="flex justify-between items-center text-base pt-6">
              <div className="font-semibold">My activity</div>
              <div className="pr-4">
                <Link
                  className="text-primary-400 text-sm"
                  to={`/organizations/${orgDetails?.id}`}
                  state={{
                    tab: OrganisationTab.Activity,
                    from: "dashboard"
                  }}
                >
                  View all Activities
                </Link>
              </div>
            </div>
            <div className="flex-1 pt-3">
              <If
                condition={!!orgActivities?.length}
                then={
                  <>
                    <div className="pr-6 bg-white max-h-96 overflow-auto">
                      <Table
                        data={orgActivities!}
                        columns={columns}
                        viewType="table"
                        pageSize={9999}
                        dropdownFilterColumnId="type"
                        shrinkFilterSort
                        noHeader
                        searchBarVisible={false}
                      />
                    </div>

                    <div
                      className="flex items-center text-primary-400 font-medium cursor-pointer text-sm select-none py-4 border-t border-neutral-200"
                      onClick={refetchOrgActivities}
                    >
                      <RefreshCw size={20} />
                      <span className="ml-2">
                        Refresh to load recent activity
                      </span>
                    </div>
                  </>
                }
                else={
                  <div className="h-full flex-1 flex items-center justify-center">
                    No activities yet
                  </div>
                }
              />
            </div>
          </div>
        }
      />

      {/* Side panel with Activity details */}
      <SidePanel
        title="Activity details"
        open={!!selectedActivity}
        onClose={clearSelectedActivity}
      >
        <ActivityDetailsFull data={activityDetails} />
      </SidePanel>
    </>
  )
}

export default DashboardActivityTable
